// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { paginatedResultSchema } from '@/api/paging';
import { parseResponse } from '@/api/parse-response';
import { SortDirection } from '@/constants/sort';
import { sparseLocationWithParentSchema } from '@/sparse/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Movement type enum matching backend InventoryMovementType.
 */
const movementTypeSchema = z.enum([
  'RECEIVE',
  'CONSUME',
  'TRANSFER',
  'ADJUST',
  'RETURN',
  'SHIP',
  'DELIVER',
]);

export type MovementType = z.infer<typeof movementTypeSchema>;

/**
 * Schema for a single movement in the history.
 */
const movementSchema = z.object({
  id: z.string(),
  movedAt: dateTimeSchema,
  movementType: movementTypeSchema,
  fromLocation: sparseLocationWithParentSchema.nullable(),
  toLocation: sparseLocationWithParentSchema.nullable(),
  reasonNote: z.string().nullable(),
  quantity: z.number(),
});

export type Movement = z.infer<typeof movementSchema>;

/**
 * Schema for the paginated movement history response.
 */
const movementHistorySchema = paginatedResultSchema(movementSchema);

/**
 * Schema for the API response wrapper.
 */
const movementHistoryResponseSchema = z
  .object({ data: movementHistorySchema })
  .transform(({ data }) => data);

type MovementHistoryResponse = z.infer<typeof movementHistorySchema>;

/**
 * Filter parameters for the movement history.
 */
export interface MovementHistoryFilter {
  page?: number;
  limit?: number;
  sortOrder?: SortDirection;
}

/**
 * Default page size for movement history.
 */
export const DEFAULT_MOVEMENT_PAGE_SIZE = 50;

/**
 * Fetches movement history for a bulk item.
 */
export async function getItemMovementHistory(
  itemKey: string,
  filter?: MovementHistoryFilter
): Promise<MovementHistoryResponse> {
  const url = `/v1/catalog/item/${itemKey}/movement`;
  const response = await api.get(url, {
    params: {
      page: filter?.page ?? 1,
      limit: filter?.limit ?? DEFAULT_MOVEMENT_PAGE_SIZE,
      sortOrder: filter?.sortOrder ?? 'desc',
    },
  });

  return parseResponse(movementHistoryResponseSchema, response.data, {
    schemaName: 'ItemMovementHistory',
    endpoint: url,
  });
}
